import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Folder, Image } from 'lucide-react';
import { DataTable } from '@/components/ui/data-table';
import { createFoldersColumns } from './FoldersColumns';
import { useTranslation } from 'react-i18next';

const defaultUsers = [];

export default function FoldersTable({
    className = '',
    items,
    depth,
    isSuperAdmin = false,
    adminTemplateMap,
    onNavigate,
    onOpenPhoto,
    onEdit,
    onMove,
    canMoveItem,
    users = defaultUsers,
    onAssignUser,
    assigningFolderId,
    onDownloadPhoto,
    onViewCertificate,
    onArchivePhoto,
    onArchiveItem,
    onRestoreItem,
    onStatusChange,
    isLoading = false,
    isLoadingPhotos = false,
    sorting,
    onSortingChange,
    manualSorting = false,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Crée les colonnes avec les callbacks
    const columns = createFoldersColumns({
        depth,
        users,
        isSuperAdmin,
        adminTemplateMap,
        onEdit,
        onMove,
        canMoveItem,
        onAssignUser,
        assigningFolderId,
        onNavigate,
        onOpenPhoto,
        onDownloadPhoto,
        onViewCertificate,
        onArchivePhoto,
        onArchiveItem,
        onRestoreItem,
        onStatusChange,
    });

    // Gestion du clic sur une ligne
    const handleRowClick = (item) => {
        if (item.type === 'folder' || item.type === 'intervention') {
            onNavigate(item.id);
            return;
        }

        if (item.type === 'procedure_report') {
            // Naviguer vers la page de détail du rapport de procédure
            const procedureId = item.procedureTemplateId;
            const returnTo = location.pathname;
            if (procedureId) {
                navigate(`/procedures/${procedureId}/reports/${item.id}`, { state: { returnTo } });
            } else {
                // Fallback si pas d'id - essayer quand même avec l'item id
                navigate(`/procedures/_/reports/${item.id}`, { state: { returnTo } });
            }
            return;
        }

        if (item.type === 'photo' && onOpenPhoto) {
            onOpenPhoto(item);
        }
    };

    // Séparer les items : dossiers/procédures vs photos
    const folders = useMemo(() => items.filter((item) => item.type !== 'photo'), [items]);
    const photos = useMemo(() => items.filter((item) => item.type === 'photo'), [items]);

    // Si on est dans un sous-dossier et qu'on charge les photos, ajouter une ligne skeleton
    const showPhotosLoader = isLoadingPhotos && depth > 0;
    const dataWithSkeleton = useMemo(() => {
        if (showPhotosLoader) {
            return [...items, { id: 'loading-skeleton', type: 'loading_skeleton', name: t('folders:chargementDesPhotos') }];
        }
        return items;
    }, [items, showPhotosLoader, t]);

    // Message et icône pour l'état vide
    // Si on charge les photos et qu'il y a des dossiers, ne pas afficher "Aucune image"
    const showEmptyState = !isLoading && !isLoadingPhotos && items.length === 0;
    const emptyMessage = depth === 0 ? t('folders:aucunDossierTrouve') : t('folders:aucuneImageTrouvee');
    const emptyIcon =
        depth === 0 ? (
            <Folder className="w-12 h-12 text-muted-foreground" />
        ) : (
            <Image className="w-12 h-12 text-muted-foreground" />
        );

    return (
        <div className={`w-full ${className}`}>
            <div className="rounded-lg border border-border bg-card">
                <DataTable
                    columns={columns}
                    data={dataWithSkeleton}
                    onRowClick={handleRowClick}
                    emptyMessage={showEmptyState ? emptyMessage : null}
                    emptyIcon={showEmptyState ? emptyIcon : null}
                    showPagination={true}
                    pageSize={10}
                    isLoading={isLoading}
                    manualSorting={manualSorting}
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    className="[&_td]:p-2! [&_td:first-child]:ps-4! [&_td:last-child]:pe-4! [&_td]:text-[13px]"
                />
            </div>
        </div>
    );
}
